'use client'
import React from 'react'
import Typography from 'src/components/design-system/Typography'
import { AuthorListItem } from 'src/components/authors/list/AuthorListItem'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import Button from 'src/components/design-system/Button'
import LINKS from 'src/_helpers/links'
import useJournalDomainRoutes from 'src/components/journals/domain/useJournalDomainRoutes'
import { buildLocalizedPath } from 'src/_helpers/localizedLinks'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { orderResourcesByUuid } from 'src/_helpers/orderResourcesByUuid'
const getAffiliations = (author) => {
  return orderResourcesByUuid(author?.affiliations, author?.affiliationOrder)
}
const getAddress = (author) => author?.address
export default function EditorialCouncil({ block, locale, variant = 'journal' }) {
  const translation = getPreferredTranslation(block, locale) || {}
  const translate = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const isDomainJournal = variant === 'journal-domain'
  const title = translation?.title?.trim()
  const editorialGroups = (block.editorialCouncilGroups || [])
    .map((group) => ({
      ...group,
      editors: (group.editors || []).filter((editor) => editor?.data),
    }))
    .filter((group) => group.editors.length)
  const buildColumnBorders = (index) => {
    const classes = []

    if (index % 2 === 1) {
      classes.push('t:border-l', 't:border-basic-gray8', 't:pl-20')
    }

    if (index % 4 !== 0) {
      classes.push('d:border-l', 'd:border-basic-gray8', 'd:pl-20')
    }

    return classes.join(' ')
  }
  const numberSlug = getPreferredTranslation(block.number, locale)?.slug || null
  const journalSlug = getPreferredTranslation(block.journal, locale)?.slug || null
  const routeMaps = variant === 'journal-domain' ? journalRoutes : null
  const linkEditorialCouncil =
    buildLocalizedPath(routeMaps?.ISSUES.SCIENTIFIC_ADVISORY_BOARD || LINKS.ISSUES.SCIENTIFIC_ADVISORY_BOARD, locale, {
      slug: journalSlug,
      issue: numberSlug,
    }) ||
    buildLocalizedPath(
      routeMaps?.JOURNALS.SCIENTIFIC_ADVISORY_BOARD || LINKS.JOURNALS.SCIENTIFIC_ADVISORY_BOARD,
      locale,
      {
        journal: journalSlug,
      },
    )

  if (!editorialGroups.length) return null

  return (
    <section
      className={`py-64 d:px-40 px-16 bg-basic-gray10/50 full-width-shadow-basic-white ${block.type.value}`}
      id={`${block.uuid}`}
    >
      {title && (
        <Typography
          variant="h2-caps-bold"
          color="text-basic-black"
          className="pb-32 t:pb-64 xl:max-w-[950px] max-w-665"
        >
          {title}
        </Typography>
      )}
      {editorialGroups.length > 0 ? (
        <div className="flex flex-col">
          {editorialGroups.slice(0, 2).map((section, key) => {
            const translationSection = getPreferredTranslation(section, locale) || {}
            const titleSection = translationSection?.title
            const editors = section.editors
            return (
              <div key={key}>
                {titleSection && (
                  <Typography variant="h3-caps-bold" color="text-dark-blue-dark-blue1" className="pb-40">
                    {titleSection}
                  </Typography>
                )}
                <div
                  className={`grid gap-24 ${key === 0 ? 'border-y' : 'border-b'} border-basic-gray8 t:grid-cols-2 d:grid-cols-4 py-24`}
                >
                  {editors.map((editor, i) => {
                    const columnBorders = buildColumnBorders(i)
                    const translationEditor = getPreferredTranslation(editor, locale) || {}
                    const position = translationEditor?.title
                    const dataEditor = editor.data
                    const translationDataEditor = getPreferredTranslation(dataEditor, locale) || {}
                    const orcid = dataEditor?.orcid
                    const fullname = dataEditor?.fullName
                    const affiliation = getAffiliations(dataEditor)
                    const address = getAddress(dataEditor)
                    const email = dataEditor?.email
                    const resolvedAffiliations = affiliation.length ? affiliation : []
                    const slug = dataEditor?.slug
                    const detailUrl =
                      buildLocalizedPath(routeMaps?.JOURNALS.COUNCIL_DETAIL || LINKS.COUNCIL.DETAIL, locale, {
                        slug,
                      }) || ' '
                    const desc = translationDataEditor.text
                    return (
                      <AuthorListItem
                        key={i}
                        name={fullname}
                        position={position}
                        orcid={orcid}
                        affiliations={resolvedAffiliations}
                        address={address}
                        email={email}
                        detailUrl={detailUrl}
                        locale={locale}
                        className={`pb-16 ${columnBorders}`}
                        desc={desc}
                      />
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      ) : null}
      {linkEditorialCouncil && journalSlug && editorialGroups.length > 2 ? (
        <div className="py-24 mt-64">
          <Button
            icon="right"
            size="l"
            className={`flex mx-auto relative w-fit ${
              isDomainJournal
                ? '!border-basic-black !bg-basic-gray10/50 !text-basic-black hover:!bg-basic-gray8'
                : 'bg-basic-gray10/50 hover:bg-basic-white'
            }`}
            href={linkEditorialCouncil}
          >
            {translate('T_SEE_FULL_COUNCIL')}
          </Button>
        </div>
      ) : null}
    </section>
  )
}
